import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const saras: Competitor = {
  "id": "saras",
  "name": "Saras Analytics",
  "group": "ecomm",
  "website": "https://www.sarasanalytics.com/",
  "owner": "Independent, privately held (founded 2016 in Hyderabad, India; no acquisition found)",
  "summary": "Saras Analytics sells an ecommerce data stack in three products: Saras Daton (ELT with 200+ connectors into your own warehouse), Saras Pulse (prebuilt ecommerce datasets and dashboards) and Saras iQ (an AI analyst over certified data, the current lead product). It targets Shopify and Amazon brands from roughly $1M to $500M in revenue.",
  "bestFor": [
    "Brands that want ecommerce-specific pipelines (Amazon Selling Partner, Amazon Ads, Walmart, TikTok Shop, Recharge, NetSuite) landing in their own BigQuery or Snowflake without writing connectors",
    "$10M-$500M Shopify brands that want a managed, governed data model plus a natural-language analyst rather than hiring a data team (Saras iQ)",
    "Teams that already have a warehouse and BI tool and only need cheap, row-priced ecommerce ELT (Daton Lite or Growth)"
  ],
  "notFor": [
    "Brands that need pixel-based multi-touch attribution, MMM or incrementality testing; Saras's own comparison says these are not in iQ Essentials and suggests pairing with an attribution tool",
    "Very small brands that only want a few dashboards; iQ starts at about $1,999/month",
    "Buyers who want one stable, clearly packaged product; Saras's product lineup and pricing pages have been reorganized around iQ in 2026"
  ],
  "strengths": [
    "Daton's long-tail ecommerce connectors (TikTok Shop, Walmart WFS, Amazon Seller/Vendor/Ads/DSP, Recharge, Stay AI, NetSuite, Stord, Fulfil, Cin7 Core) are maintained by Saras, with monthly release notes fixing API changes that a custom build would otherwise absorb",
    "Daton loads into your own Snowflake, BigQuery or Redshift, so the data stays reusable by any BI tool, and Daton Lite is free up to 1M rows/month",
    "Saras iQ includes a prebuilt ecommerce data model, certified metric definitions and validation against 30-100 historical questions, with a stated go-live of about 10 business days to three weeks versus months for a custom dbt project",
    "iQ answers plain-English questions in a web app, Slack or Claude (via iQ MCP) and shows the SQL it ran, which reduces analyst ticket load",
    "Pulse Growth pricing ($250-$1,500/month by revenue band) undercuts the cost of a consultant-built warehouse plus BI licenses for smaller brands"
  ],
  "limitations": [
    "Saras's own iQ comparison states multi-touch attribution, MMM and incrementality testing are not included in iQ Essentials",
    "On Pulse Growth and Trial, data sits in a Saras-managed warehouse; using your own warehouse is listed only for Pulse Enterprise",
    "Saras's comparison lists iQ Essentials on BigQuery or Snowflake only, with other warehouses and the NetSuite connector reserved for Enterprise",
    "Custom semantic/context layers, role-based team access and custom source integrations are Enterprise-only for iQ, so business-specific logic beyond the certified model costs more",
    "Pulse's documented live connector list is shorter than Daton's (Amazon, Shopify, Meta, Google, Bing, Klaviyo, GA, Sheets) with several items marked coming soon",
    "Product naming and pricing pages are in flux: the Pulse pricing URL now shows iQ pricing, and help articles give conflicting Pulse trial lengths (14 vs 31 days)"
  ],
  "pricing": {
    "model": "Revenue-band tiers for Pulse and iQ; row-based usage pricing for Daton",
    "publicStartingPrice": "Daton Lite free (1M rows/month); Daton Growth $95/month; Pulse Growth from $250/month ($1M revenue band); Saras iQ Essentials from $1,999/month",
    "freeTier": "Yes: Daton Lite is free up to 1M rows/month. Pulse has a free trial (help center states both 14 and 31 days). iQ trial includes 10,000 query credits over 3 months.",
    "notes": "Daton: Lite $0 (1M rows, extra $33/M rows, basic connectors), Growth $95/month (shown as $79, likely annual rate; 5M-100M row bands, extra $28.5/M rows, 150+ connectors), Enterprise custom; cost driven by rows loaded. Pulse (per help center): Growth priced by annual revenue, $250/month at $1M up to $1,500/month at $10M, about two months free on annual billing; Enterprise custom, adds attribution and DSP data and own-warehouse option. Saras iQ: Essentials from $1,999/month for $10M-$50M Shopify brands; Enterprise custom for $50M-$500M brands, adding custom semantic layer, 200+ advanced integrations, multi-entity and role-based access. Setup: Saras states about 10 business days for iQ on its product page and about three weeks managed setup in its Zenlytic comparison.",
    "sourceUrl": "https://help.sarasanalytics.com/en_US/billing-and-subscription/pulse-subscription-plan-overview",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Live Shopify connector in Pulse and Daton",
      "sourceUrl": "https://help.sarasanalytics.com/en_US/billing-and-subscription/pulse-subscription-plan-overview"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon Selling Partner, Vendor Central, Sponsored Ads; DSP on Pulse Enterprise",
      "sourceUrl": "https://help.sarasanalytics.com/en_US/billing-and-subscription/pulse-subscription-plan-overview"
    },
    "tiktok_shop": {
      "value": "yes",
      "note": "Daton connector for orders, products, packages, statements, payments",
      "sourceUrl": "https://www.sarasanalytics.com/daton/tiktok-shop"
    },
    "wholesale_retail_or_erp": {
      "value": "yes",
      "note": "Daton connectors for NetSuite, Walmart, Stord, Fulfil, Cin7 Core, Mintsoft and others; NetSuite is Enterprise-only in iQ; Faire not found",
      "sourceUrl": "https://sarasanalytics.com/daton/netsuite"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta, Google, Bing ads in Pulse; TikTok Ads via Daton",
      "sourceUrl": "https://knowledge-saras-analytics.helpjuice.com/tiktok-ads"
    },
    "first_party_pixel_attribution": {
      "value": "no",
      "note": "Saras says MTA is not in iQ Essentials and suggests pairing with an attribution tool; no own pixel documented (Pulse Enterprise lists 'Attribution' without detail)",
      "sourceUrl": "https://www.sarasanalytics.com/blog/saras-iq-vs-zenlytic"
    },
    "mmm_incrementality": {
      "value": "no",
      "note": "Saras says MMM and incrementality testing are not in iQ Essentials; not documented elsewhere",
      "sourceUrl": "https://www.sarasanalytics.com/blog/saras-iq-vs-zenlytic"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Contribution margin is a core iQ use case",
      "sourceUrl": "https://www.sarasanalytics.com/saras-iq-new"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "Customer cohorts with LTV and CAC in iQ",
      "sourceUrl": "https://www.sarasanalytics.com/saras-iq-new"
    },
    "subscription_analytics": {
      "value": "partial",
      "note": "Recharge and Stay AI connectors load data to your warehouse; prebuilt subscription dashboards not confirmed",
      "sourceUrl": "https://www.sarasanalytics.com/daton/recharge-payments"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Daton loads to your Snowflake/BigQuery/Redshift; Pulse Growth uses Saras-managed warehouse, own warehouse on Enterprise",
      "sourceUrl": "https://www.sarasanalytics.com/saras-daton/pricing"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "iQ uses certified definitions set up with Saras; custom semantic and context layers are Enterprise-only",
      "sourceUrl": "https://www.sarasanalytics.com/pulse/pricing"
    },
    "custom_sql": {
      "value": "partial",
      "note": "iQ shows the SQL it generates; ad-hoc SQL is done in your own warehouse, not an in-product editor",
      "sourceUrl": "https://www.sarasanalytics.com/blog/introducing-saras-iq-ai-data-team-for-ecommerce"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Saras iQ: AI analyst in web app, Slack and via iQ MCP in Claude",
      "sourceUrl": "https://www.sarasanalytics.com/saras-iq-new"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app found; iQ is reachable in Slack",
      "sourceUrl": "https://www.sarasanalytics.com/products"
    }
  },
  "recentChanges": [
    {
      "date": "2026",
      "change": "Launched Saras iQ, an AI analyst (web app, Slack, iQ MCP) over certified ecommerce data, now the lead product; Pulse repositioned as AI-ready datasets and dashboards",
      "sourceUrl": "https://www.sarasanalytics.com/blog/introducing-saras-iq-ai-data-team-for-ecommerce"
    },
    {
      "date": "2026",
      "change": "New iQ pricing: Essentials from $1,999/month for $10M-$50M brands, Enterprise custom; the Pulse pricing URL now shows iQ plans",
      "sourceUrl": "https://www.sarasanalytics.com/pulse/pricing"
    },
    {
      "date": "2026-06",
      "change": "Daton release adds Stord connector and Walmart WFS fields, plus fixes for Recharge, TikTok Shop and Amazon connectors",
      "sourceUrl": "https://help.sarasanalytics.com/en_US/2026/-release-notes-daton-june-2026"
    }
  ],
  "sources": [
    {
      "title": "Saras products",
      "url": "https://www.sarasanalytics.com/products"
    },
    {
      "title": "Saras iQ / Pulse pricing page",
      "url": "https://www.sarasanalytics.com/pulse/pricing"
    },
    {
      "title": "Saras Daton Pricing",
      "url": "https://www.sarasanalytics.com/saras-daton/pricing"
    },
    {
      "title": "Pulse subscription plan overview (help center)",
      "url": "https://help.sarasanalytics.com/en_US/billing-and-subscription/pulse-subscription-plan-overview"
    },
    {
      "title": "Pulse free trial and pricing (help center)",
      "url": "https://help.sarasanalytics.com/en_US/getting-started/free-trial-and-pricing"
    },
    {
      "title": "Meet Saras iQ",
      "url": "https://www.sarasanalytics.com/saras-iq-new"
    },
    {
      "title": "Introducing Saras iQ",
      "url": "https://www.sarasanalytics.com/blog/introducing-saras-iq-ai-data-team-for-ecommerce"
    },
    {
      "title": "Saras iQ vs Zenlytic",
      "url": "https://www.sarasanalytics.com/blog/saras-iq-vs-zenlytic"
    },
    {
      "title": "Saras Daton",
      "url": "https://www.sarasanalytics.com/saras-daton"
    },
    {
      "title": "Daton TikTok Shop connector",
      "url": "https://www.sarasanalytics.com/daton/tiktok-shop"
    },
    {
      "title": "Daton NetSuite connector",
      "url": "https://sarasanalytics.com/daton/netsuite"
    },
    {
      "title": "Daton Recharge connector",
      "url": "https://www.sarasanalytics.com/daton/recharge-payments"
    },
    {
      "title": "Daton TikTok Ads connector",
      "url": "https://knowledge-saras-analytics.helpjuice.com/tiktok-ads"
    },
    {
      "title": "Daton release notes June 2026",
      "url": "https://help.sarasanalytics.com/en_US/2026/-release-notes-daton-june-2026"
    },
    {
      "title": "Telangana Today profile of Saras Analytics (2021)",
      "url": "https://telanganatoday.com/telangana-trio-helps-enterprises-decode-data"
    }
  ],
  "lastReviewed": "2026-11-03"
};
